import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { AuthorisationStates } from '@/constants'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useSpace } from '@/hooks/useSpace'
import useConsent from '@/hooks/useConsent'
import { RootState } from '@/utils/store/store'
import { checkServer } from '@/utils/serverReachability'
import type { StartupStep, StartupStepStatus } from '@/ui/components/StartupSplash/types'

type ServerStatus = 'checking' | 'reachable' | 'unreachable'
type StepId = 'server' | 'signIn' | 'spaces' | 'configurations'

// The sign-in leaves the page (redirect) and comes back: what is done stays done, so the steps do not start over
const SERVER_OK_KEY = 'mma_startup_server_ok'

/** The status of each step from what the app knows; a step waits until the one before it is done. */
export const deriveStartupSteps = (input: {
    server: ServerStatus
    signedIn: boolean
    /** The sign-in is running, or about to: the app starts, or the user is on the way to / back from the login */
    signInInProgress: boolean
    signInFailed: boolean
    spacesLoaded: boolean
    spacesFailed: boolean
    spaceActive: boolean
    configurationsFailed: boolean
}): Record<StepId, StartupStepStatus> => {
    const server: StartupStepStatus = input.server === 'reachable' ? 'done' : input.server === 'unreachable' ? 'error' : 'loading'
    const serverDone = server === 'done'

    let signIn: StartupStepStatus = 'pending'
    if (input.signedIn) signIn = 'done'
    else if (input.signInFailed) signIn = 'error'
    else if (serverDone && input.signInInProgress) signIn = 'loading'
    const signInDone = signIn === 'done'

    let spaces: StartupStepStatus = 'pending'
    if (signInDone) spaces = input.spacesLoaded ? 'done' : input.spacesFailed ? 'error' : 'loading'
    const spacesDone = spaces === 'done'

    let configurations: StartupStepStatus = 'pending'
    if (spacesDone) configurations = input.configurationsFailed ? 'error' : input.spaceActive ? 'done' : 'loading'

    return { server, signIn, spaces, configurations }
}

// The consent banner often never answers on localhost: the authentication ignores it there too
// A failed sign-in leaves an object with `code` or `message` in the authentication state (it is a string otherwise)
const hasSignInFailure = (state: unknown): boolean =>
    typeof state === 'object' && state !== null
    && (('code' in state && Boolean(state.code)) || ('message' in state && Boolean(state.message)))

const isLocalDevelopment = () => import.meta.env.MODE === 'development' && ['localhost', '127.0.0.1'].includes(window.location.hostname)

/** A step that was done does not go back (a spinner that starts over looks like the app is stuck). */
export const keepDone = (kept: Partial<Record<StepId, StartupStepStatus>>, next: Record<StepId, StartupStepStatus>) => {
    const result = { ...next }
    for (const id of Object.keys(next) as StepId[]) {
        if (kept[id] === 'done' && next[id] !== 'error') result[id] = 'done'
        else kept[id] = next[id]
    }
    return result
}

/** The four startup steps for the splash: server, sign-in, spaces, configuration. */
export const useStartupSteps = (): { steps: StartupStep[]; allDone: boolean; waitingForUser: boolean; signingIn: boolean } => {
    const { t } = useTranslation()
    const rawConfig = useSelector((state: RootState) => state.application.rawConfig)
    const { authorisationFlow, authenticationState, information, loginUser } = useAuthentication()
    const { spaces, spacesError, reloadSpaces, getActiveSpace, activationError, retryActivation, isActivating } = useSpace()
    const { hasUserRespondedToConsent, isConsentInitialized } = useConsent()

    const endpoint: string | undefined = (rawConfig as { endpoint?: string } | undefined)?.endpoint
    const [server, setServer] = useState<ServerStatus>(() => (sessionStorage.getItem(SERVER_OK_KEY) ? 'reachable' : 'checking'))
    const doneRef = useRef<Partial<Record<StepId, StartupStepStatus>>>({})

    const checkConnection = useCallback(async () => {
        setServer('checking')
        const reachable = await checkServer(endpoint)
        if (reachable) sessionStorage.setItem(SERVER_OK_KEY, 'true')
        else sessionStorage.removeItem(SERVER_OK_KEY)
        setServer(reachable ? 'reachable' : 'unreachable')
    }, [endpoint])

    useEffect(() => {
        // Coming back from the login the server is known: no new "connecting" step
        if (sessionStorage.getItem(SERVER_OK_KEY)) return
        void checkConnection()
    }, [checkConnection])

    const signInFailed = hasSignInFailure(authenticationState)
    const redirecting = authorisationFlow === AuthorisationStates.AUTHORISING || sessionStorage.getItem('isAuthorizationInProgress') === 'true'
    // The app starts and does not know yet whether the user is signed in
    const initialising = authorisationFlow === AuthorisationStates.IDLE || authorisationFlow === undefined
    // Signed out and nothing is going on: the login page waits for the user to press the button
    const waitingForLogin = authorisationFlow === AuthorisationStates.RELOGIN && !redirecting && !signInFailed && !information?.userId
    // The sign-in does not start before the user answered the consent banner: the splash must not cover it
    const waitingForConsent = isConsentInitialized && !hasUserRespondedToConsent && !isLocalDevelopment()
    const waitingForUser = waitingForLogin || waitingForConsent

    const raw = deriveStartupSteps({
        server,
        signedIn: Boolean(information?.userId),
        signInInProgress: redirecting || initialising,
        signInFailed: signInFailed && !redirecting,
        spacesLoaded: spaces.length > 0,
        spacesFailed: spacesError,
        // The space is flagged active as the switch starts; it is ready when the history and the metadata are there
        spaceActive: Boolean(getActiveSpace()) && !isActivating,
        configurationsFailed: activationError,
    })
    const status = keepDone(doneRef.current, raw)

    const steps: StartupStep[] = [
        {
            id: 'server',
            title: status.server === 'done' ? t('login:startup.server.done') : status.server === 'error' ? t('login:startup.server.error') : t('login:startup.server.loading'),
            status: status.server,
            onRetry: () => {
                sessionStorage.removeItem(SERVER_OK_KEY)
                void checkConnection()
            },
        },
        {
            id: 'signIn',
            title: t('login:startup.signIn.title'),
            status: status.signIn,
            detail: t(`login:startup.signIn.${status.signIn}`),
            onRetry: () => loginUser?.(),
        },
        {
            id: 'spaces',
            title: t('login:startup.spaces.title'),
            status: status.spaces,
            detail: t(`login:startup.spaces.${status.spaces}`),
            onRetry: () => void reloadSpaces(),
        },
        {
            id: 'configurations',
            title: t('login:startup.configurations.title'),
            status: status.configurations,
            detail: t(`login:startup.configurations.${status.configurations}`),
            onRetry: () => void retryActivation(),
        },
    ]

    // The user is on the way to the login, back from it, or the sign-in failed: not someone who is signed in already
    const signingIn = redirecting || signInFailed

    return { steps, allDone: steps.every((step) => step.status === 'done'), waitingForUser, signingIn }
}
